<template>
  <div class="p-3 flex flex-col bg-key rounded-4xl">
    <div class="flex items-center gap-1 h-9 text-black-white text-l-medium">
      <Icon icon="Multimedia-Wireframe-LEDColor" class="size-6" />
      <span class="text-l-medium text-black-white">{{
        t('setting.device.productColor.title')
      }}</span>
    </div>

    <div class="min-h-10 w-full flex items-center gap-1 flex-wrap">
      <div
        v-for="(item, index) in colorSeries"
        :key="index"
        :class="[
          'px-0.5 flex items-center justify-between h-10 border-2 rounded-full transition-colors cursor-pointer',
          currentDeviceColor?.key === item.key
            ? 'border-brand-blue1'
            : 'border-transparent hover:border-blue-dark-hover',
        ]"
      >
        <button
          class="px-3 min-w-18 h-8 flex justify-center items-center rounded-full cursor-pointer"
          :style="getButtonStyle(item.value)"
          @click="handleChangeColor(item)"
        >
          <span class="text-s-medium" :class="getTextColorClass(item.value)">
            {{ t(item.name) }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import { Icon } from '@rapoo/element'
  import { useDeviceStore } from '@/stores'
  import type { ColorSeriesItem } from '@rapoo/device-sdk'
  const { t } = useI18n()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const colorSeries = computed(() => {
    const colors = currentDevice.value?.manifest?.ui.colors
    return colors ? (colors.series as ColorSeriesItem[]) : []
  })

  const currentDeviceColor = computed(() => {
    const colors = currentDevice.value?.manifest?.ui.colors
    if (!colors) return undefined
    const series = colors.series as ColorSeriesItem[]
    const currentKey = colors.current

    return series.find((color) => color.key === currentKey)
  })

  const handleChangeColor = (item: ColorSeriesItem) => {
    if (item.key) {
      deviceStore.changeDeviceManifestCurrentColor(item.key)
    }
  }

  // 根据 value 类型生成背景样式
  function getButtonStyle(value: string | string[]): Record<string, string> {
    if (Array.isArray(value)) {
      // 数组 => 渐变
      return { background: generateGradient(value) }
    }
    // 普通颜色
    return { background: value }
  }

  // 生成 linear-gradient 字符串
  function generateGradient(colors: string[]): string {
    if (colors.length === 0) return ''
    if (colors.length === 1) return colors[0]

    const stops = colors.map((color, index) => {
      const percent = (index / (colors.length - 1)) * 100
      return `${color} ${percent}%`
    })
    return `linear-gradient(0deg, ${stops.join(', ')})`
  }
  /**
   * 计算颜色数组的平均相对亮度（WCAG公式）
   */
  function getAverageLuminance(colors: string[]): number {
    if (!colors.length) return 0

    const luminances = colors.map((color) => {
      const hex = color.replace('#', '')
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)

      const toLinear = (c: number) => {
        const s = c / 255
        return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
      }
      return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
    })

    // 简单算术平均，你也可以根据渐变方向给不同权重
    return luminances.reduce((sum, l) => sum + l, 0) / luminances.length
  }

  function getTextColorClass(value: string | string[]): string {
    let colorArr: string[] = []
    if (Array.isArray(value)) {
      colorArr = value
    } else {
      colorArr = [value]
    }

    const avgLum = getAverageLuminance(colorArr)
    return avgLum > 0.3 ? 'text-black' : 'text-white'
  }
</script>

<style scoped></style>
